import type { ReviewClass } from '@rsecure/brand-contracts/console';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router';
import {
  Badge,
  Empty,
  ErrorState,
  Icon,
  Loading,
  Ltr,
  Pager,
  StatusBadge,
  Time,
} from '../components/ui';
import { api } from '../lib/api';
import { fmtNum } from '../lib/format';
import { useSearchText, useUrlState } from '../lib/url';
import { ReviewCardBody } from './brand/Reviews';

const CLASSES: Array<{ value: ReviewClass; label: string }> = [
  { value: 'identity_mismatch', label: 'Identity mismatch' },
  { value: 'parent_sub_brand', label: 'Parent vs sub-brand' },
  { value: 'domain_conflict', label: 'Domain conflict' },
  { value: 'shared_provider_entity', label: 'Shared provider entity / asset' },
  { value: 'logo', label: 'Missing / uncertain logo' },
  { value: 'legacy', label: 'Legacy open items' },
  { value: 'other', label: 'Other' },
];

function ReviewDetail({ id }: { id: string }) {
  const q = useQuery({ queryKey: ['review', id], queryFn: () => api.review(id) });
  if (q.isError) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  if (!q.data) return <Loading />;
  const { review: r, candidates, relatedEntities } = q.data;
  return (
    <section className="card drawer" aria-labelledby="h-review" data-testid="review-detail">
      <div className="card-h">
        <h2 id="h-review" className="mono" style={{ fontSize: 13 }}>
          {r.kind}
        </h2>
        <StatusBadge
          kind="review"
          value={r.status}
          tone={r.status === 'OPEN' ? (r.blocking ? 'bad' : 'warn') : undefined}
        />
      </div>
      <div className="card-b grid" style={{ gap: 12 }}>
        <dl className="dl" style={{ gridTemplateColumns: '130px minmax(0,1fr)' }}>
          <dt>Brand entity</dt>
          <dd>
            {r.entity ? (
              <>
                <Link className="link" to={`/brands/${r.entity.slug}`}>
                  {r.entity.name}
                </Link>{' '}
                <StatusBadge kind="pipeline" value={r.entity.pipelineState} />
              </>
            ) : (
              <span className="faint">no entity (catalog-level item)</span>
            )}
          </dd>
          <dt>Publication impact</dt>
          <dd>
            {r.blocking ? (
              <Badge tone="bad">Blocks publication</Badge>
            ) : (
              <Badge>Non-blocking (visual / advisory)</Badge>
            )}
            {r.entity && !r.entity.published && (
              <span className="muted"> · entity not published</span>
            )}
          </dd>
          <dt>Subject</dt>
          <dd>
            <Ltr mono>{r.subjectKey}</Ltr>
          </dd>
          <dt>Opened</dt>
          <dd>
            <Time iso={r.createdAt} />
          </dd>
          {r.revision && (
            <>
              <dt>Revision</dt>
              <dd>
                #{r.revision.number} · {r.revision.state}
              </dd>
            </>
          )}
        </dl>
        <ReviewCardBody r={r} />
        {relatedEntities.length > 0 && (
          <div>
            <strong style={{ fontSize: 13 }}>Other entity involved</strong>
            {relatedEntities.map((e) => (
              <div key={e.id}>
                <Link className="link" to={`/brands/${e.slug}`}>
                  {e.name}
                </Link>{' '}
                {e.published ? (
                  <Badge tone="ok">published</Badge>
                ) : (
                  <Badge tone="warn">unpublished</Badge>
                )}
              </div>
            ))}
          </div>
        )}
        <div>
          <strong style={{ fontSize: 13 }}>Affected candidates ({candidates.length})</strong>
          {candidates.length ? (
            <ul style={{ margin: '6px 0 0', paddingInlineStart: 18 }}>
              {candidates.map((c) => (
                <li key={c.id}>
                  {c.name} ·{' '}
                  <Ltr mono>
                    {c.sourceFile}#{c.sourceRow}
                  </Ltr>{' '}
                  · <StatusBadge kind="candidate" value={c.status} />
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted" style={{ margin: '4px 0 0' }}>
              No candidate row is linked to this entity.
            </p>
          )}
        </div>
        <p className="muted" style={{ fontSize: 12.5, margin: 0 }}>
          Read-only. Resolve with{' '}
          <Ltr mono>bi review {r.id.slice(0, 8)}… approve|reject --reason</Ltr> (audited).
        </p>
      </div>
    </section>
  );
}

export function ReviewsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { sp, set } = useUrlState();
  const [text, setText] = useSearchText();
  const limit = Number(sp.get('limit') ?? 25);
  const offset = Number(sp.get('offset') ?? 0);
  const params = {
    status: sp.get('status') ?? 'OPEN',
    class: sp.get('class'),
    kind: sp.get('kind'),
    blocking: sp.get('blocking'),
    q: sp.get('q'),
    limit,
    offset,
  };
  const list = useQuery({
    queryKey: ['reviews', params],
    queryFn: () => api.reviews(params),
    placeholderData: keepPreviousData,
  });
  const byClass = list.data?.byClass ?? {};
  const suffix = sp.toString() ? `?${sp}` : '';

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Review queue</h1>
          <p>
            Ambiguous or blocked records waiting for a human decision. Read-only in this release —
            resolve through the audited CLI/admin API.
          </p>
        </div>
      </div>
      <section className="stats" aria-label="Reviews by class">
        {CLASSES.map((c) => (
          <button
            key={c.value}
            type="button"
            className="stat"
            style={{
              textAlign: 'start',
              cursor: 'pointer',
              outline: sp.get('class') === c.value ? '2px solid var(--accent)' : undefined,
            }}
            aria-pressed={sp.get('class') === c.value}
            onClick={() => set({ class: sp.get('class') === c.value ? null : c.value, kind: null })}
          >
            <div className="label">{c.label}</div>
            <div className="value">{fmtNum(byClass[c.value] ?? 0)}</div>
          </button>
        ))}
      </section>
      <search className="filters">
        <div className="search">
          <Icon name="search" />
          <input
            className="input"
            type="search"
            aria-label="Search reviews"
            placeholder="Search summary, subject or brand"
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </div>
        <select
          className="select"
          aria-label="Status"
          value={params.status}
          onChange={(e) => set({ status: e.target.value })}
        >
          {['OPEN', 'APPROVED', 'REJECTED', 'SUPERSEDED', 'ALL'].map((s) => (
            <option key={s} value={s}>
              Status: {s.toLowerCase()}
            </option>
          ))}
        </select>
        <select
          className="select"
          aria-label="Publication impact"
          value={sp.get('blocking') ?? ''}
          onChange={(e) => set({ blocking: e.target.value || null })}
        >
          <option value="">Impact: all</option>
          <option value="true">Blocks publication</option>
          <option value="false">Non-blocking</option>
        </select>
        {sp.get('kind') && (
          <span className="chip">
            kind: {sp.get('kind')}
            <button type="button" aria-label="Clear kind" onClick={() => set({ kind: null })}>
              ×
            </button>
          </span>
        )}
      </search>
      <div className={id ? 'split' : undefined}>
        <section className="card">
          {list.isError ? (
            <ErrorState error={list.error} onRetry={() => list.refetch()} />
          ) : !list.data ? (
            <Loading rows={10} />
          ) : list.data.total === 0 ? (
            <Empty title="No reviews match these filters" />
          ) : (
            <>
              <div className="table-wrap">
                <table className="t">
                  <thead>
                    <tr>
                      <th scope="col">Review</th>
                      <th scope="col">Brand</th>
                      <th scope="col">Impact</th>
                      <th scope="col" className="hide-sm">
                        Opened
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.data.items.map((r) => (
                      <tr
                        key={r.id}
                        className={`row-link${r.id === id ? ' selected' : ''}`}
                        onClick={() => navigate(`/reviews/${r.id}${suffix}`)}
                        data-testid="review-row"
                      >
                        <td style={{ maxWidth: 520 }}>
                          <div
                            style={{
                              display: 'flex',
                              gap: 6,
                              alignItems: 'center',
                              flexWrap: 'wrap',
                            }}
                          >
                            <button
                              type="button"
                              className="badge"
                              style={{ border: 0, cursor: 'pointer' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                set({ kind: r.kind, class: null });
                              }}
                            >
                              {r.kind}
                            </button>
                            <StatusBadge
                              kind="review"
                              value={r.status}
                              tone={r.status === 'OPEN' ? (r.blocking ? 'bad' : 'warn') : undefined}
                            />
                          </div>
                          <Link
                            to={`/reviews/${r.id}${suffix}`}
                            onClick={(e) => e.stopPropagation()}
                            style={{ display: 'block', marginTop: 4, fontSize: 13 }}
                          >
                            {r.summary}
                          </Link>
                        </td>
                        <td>
                          {r.entity ? (
                            <>
                              <div style={{ fontWeight: 600 }}>{r.entity.name}</div>
                              <StatusBadge kind="pipeline" value={r.entity.pipelineState} />
                            </>
                          ) : (
                            <span className="faint">—</span>
                          )}
                          {r.affectedCandidates > 0 && (
                            <div className="muted" style={{ fontSize: 12 }}>
                              {r.affectedCandidates} candidate(s)
                            </div>
                          )}
                        </td>
                        <td>
                          {r.blocking ? (
                            <Badge tone="bad">blocking</Badge>
                          ) : (
                            <Badge>advisory</Badge>
                          )}
                        </td>
                        <td className="hide-sm">
                          <Time iso={r.createdAt} relative />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pager
                total={list.data.total}
                limit={limit}
                offset={offset}
                onChange={(o) => set({ offset: String(o) }, false)}
                onLimit={(l) => set({ limit: String(l) })}
              />
            </>
          )}
        </section>
        {id && <ReviewDetail id={id} />}
      </div>
    </>
  );
}
